import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import intl from 'react-intl-universal';
import useModal from 'Hooks/useModal';
import {
  getRequest,
  getRequestQuestions,
  resetActiveRequest,
} from 'state/ActiveRequest/activeRequestSlice';
import { getRequestPermissions } from 'state/normalized/requestPermissions/requestPermissionsSlice';
import ViewRequestModal from '../components/ViewRequestModal/ViewRequestModal';
import AdvancedRequestModalIframe from '../components/AdvancedRequestModalIframe/AdvancedRequestModalIframe';

type UseLinkedRequestModalsOptions = {
  preloadAdvanced?: boolean;
};

const useLinkedRequestModals = ({
  preloadAdvanced = false,
}: UseLinkedRequestModalsOptions = {}) => {
  const dispatch = useDispatch();

  const {
    Modal: ViewRequestModalChrome,
    modalProps: viewRequestModalProps,
    openModal: openViewRequestModal,
  } = useModal();

  const handleOpenLegacyRequest = useCallback(
    (requestId: string) => {
      dispatch(getRequest(requestId));
      dispatch(getRequestQuestions(requestId));
      dispatch(getRequestPermissions({ requestIds: [requestId] }));

      openViewRequestModal({
        title: intl.get('NEW_PROJECT_PAGE.VIEW_PROJECT_MODAL.TITLE'),
        size: 'large',
        'data-cy': 'view-linked-request-modal',
        children: <ViewRequestModal />,
        childrenClassName: 'p-0 max-h-screen-minus-16rem',
      } as unknown as Parameters<typeof openViewRequestModal>[0]);
    },
    [dispatch, openViewRequestModal]
  );

  const wasViewRequestModalOpenRef = useRef(false);
  useEffect(() => {
    if (wasViewRequestModalOpenRef.current && !viewRequestModalProps.isOpen) {
      dispatch(resetActiveRequest());
    }
    wasViewRequestModalOpenRef.current = viewRequestModalProps.isOpen ?? false;
  }, [viewRequestModalProps.isOpen, dispatch]);

  const [isAdvancedModalOpen, setIsAdvancedModalOpen] = useState(false);
  const [advancedRequestId, setAdvancedRequestId] = useState<string | null>(
    null
  );

  const handleOpenAdvancedRequest = useCallback((requestId: string) => {
    setAdvancedRequestId(requestId);
    setIsAdvancedModalOpen(true);
  }, []);

  const handleCloseAdvancedModal = useCallback(() => {
    setIsAdvancedModalOpen(false);
  }, []);

  const modals = useMemo(
    () => (
      <>
        <ViewRequestModalChrome
          {...viewRequestModalProps}
          aria-label={intl.get('NEW_PROJECT_PAGE.VIEW_PROJECT_MODAL.TITLE')}
        />
        <AdvancedRequestModalIframe
          isOpen={isAdvancedModalOpen}
          requestId={advancedRequestId}
          onClose={handleCloseAdvancedModal}
          preload={preloadAdvanced}
        />
      </>
    ),
    [
      ViewRequestModalChrome,
      viewRequestModalProps,
      isAdvancedModalOpen,
      advancedRequestId,
      handleCloseAdvancedModal,
      preloadAdvanced,
    ]
  );

  return {
    onOpenLegacyRequest: handleOpenLegacyRequest,
    onOpenAdvancedRequest: handleOpenAdvancedRequest,
    modals,
  };
};

export default useLinkedRequestModals;
